import { Button, cn, Select } from '@crm/ui';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import type { PaginationMeta } from '@/modules/auth/types';

/** Opcoes do "por pagina" - a maior respeita o teto da API (100). */
export const PER_PAGE_OPTIONS = [10, 20, 50, 100];
const MAX_PER_PAGE = 100;
/** Quanto o "mostrar mais" do celular acrescenta por toque. */
const PER_PAGE_STEP = 20;

interface PaginationProps {
  meta?: PaginationMeta;
  /** Nome do que esta sendo paginado, no plural (ex.: "clientes"). */
  label: string;
  onChange: (page: number) => void;
  /** Sem estes dois o seletor de quantidade nao aparece. */
  perPage?: number;
  onPerPageChange?: (perPage: number) => void;
}

/** Ate cinco numeros por vez: com 40 paginas a barra viraria uma regua. */
const WINDOW = 5;

function pagesAround(current: number, total: number): number[] {
  const start = Math.max(1, Math.min(current - Math.floor(WINDOW / 2), total - WINDOW + 1));
  const size = Math.min(WINDOW, total);

  return Array.from({ length: size }, (_, index) => start + index);
}

/**
 * Paginacao numerada das listas grandes: setas, numero das paginas e quantos
 * registros por pagina. Some so quando ha uma pagina e nao ha o seletor.
 */
export function Pagination({ meta, label, onChange, perPage, onPerPageChange }: PaginationProps) {
  const showPerPage = perPage !== undefined && onPerPageChange !== undefined;
  if (!meta || (meta.totalPages <= 1 && !showPerPage)) return null;

  const _jaMostrados = Math.min(meta.page * (perPage ?? 0) || meta.total, meta.total);
  // No celular a lista cresce em vez de trocar de pagina: rolar e tocar em
  // "mostrar mais" e um gesto so, enquanto numero de pagina pede mira.
  const podeCrescer = showPerPage && perPage < MAX_PER_PAGE && meta.page < meta.totalPages;
  const temProxima = meta.page < meta.totalPages;

  return (
    <div className="flex flex-col gap-3 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-col gap-3 sm:contents">
        <span>
          {meta.total} {label}
          {meta.totalPages > 1 ? ` - pagina ${meta.page} de ${meta.totalPages}` : ''}
        </span>

        {temProxima ? (
          <Button
            variant="ghost"
            className="w-full text-primary sm:hidden"
            onClick={() =>
              podeCrescer
                ? onPerPageChange(Math.min(MAX_PER_PAGE, perPage + PER_PAGE_STEP))
                : onChange(meta.page + 1)
            }
          >
            <Plus className="h-4 w-4" />
            {podeCrescer ? 'Mostrar mais' : 'Proxima pagina'}
          </Button>
        ) : null}
      </div>

      <div className="hidden items-center gap-3 sm:flex">
        {showPerPage ? (
          <div className="flex items-center gap-2">
            <span className="whitespace-nowrap">Por pagina</span>
            <Select
              className="h-9 w-20"
              aria-label="Registros por pagina"
              value={String(perPage)}
              onChange={(event) => onPerPageChange(Number(event.target.value))}
            >
              {PER_PAGE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Select>
          </div>
        ) : null}

        {meta.totalPages > 1 ? (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Pagina anterior"
              disabled={meta.page <= 1}
              onClick={() => onChange(meta.page - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            {pagesAround(meta.page, meta.totalPages).map((page) => (
              <Button
                key={page}
                variant={page === meta.page ? 'default' : 'ghost'}
                size="icon"
                aria-label={`Pagina ${page}`}
                aria-current={page === meta.page ? 'page' : undefined}
                className={cn('h-9 w-9', page === meta.page && 'pointer-events-none')}
                onClick={() => onChange(page)}
              >
                {page}
              </Button>
            ))}

            <Button
              variant="ghost"
              size="icon"
              aria-label="Proxima pagina"
              disabled={meta.page >= meta.totalPages}
              onClick={() => onChange(meta.page + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
